import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Icon from "../ui/Icon";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";

const UniversityManagerSidebar = ({ isCollapsed, onToggle }) => {
  const location = useLocation();
  const { t } = useTranslation(['navigation', 'common']);
  const uniAdminAuth = useUniAdminAuth();
  const mainAuth = useAuth();
  const isSuperAdmin = uniAdminAuth?.isSuperAdmin;
  const university = uniAdminAuth?.university;
  // Defaults to () => true on the mock /university-manager demo tree (see
  // UniAdminAuthContext's default context value), so it's always safe to
  // call here regardless of which tree this sidebar is rendered in.
  const can = uniAdminAuth?.can ?? (() => true);

  const handleLogout = () => {
    if (location.pathname.startsWith("/uni-admin") && uniAdminAuth?.logout) {
      uniAdminAuth.logout();
    } else if (uniAdminAuth?.logout) {
      uniAdminAuth.logout();
    } else if (mainAuth?.logout) {
      mainAuth.logout();
    } else {
      window.location.href = "/uni-admin/login";
    }
  };

  const [pendingCount] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isActiveRoute = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const basePath = location.pathname.startsWith("/uni-admin") ? "/uni-admin" : "/university-manager";
  // Yetki Yönetimi is a real-backend-only feature with no mock/demo story, so
  // it's never shown on the mock /university-manager tree, and on the real
  // /uni-admin tree it's gated on the caller actually being a Super Admin.
  const showAuthorityNav = basePath === "/uni-admin" && isSuperAdmin;

  // Section-level visibility, driven by each page's own top-level `can()`
  // gate (see the mapping in each UM*.jsx page) so a nav entry never links
  // to a page the current admin would just see a "permission denied" on.
  const showCampuses = can(PERMISSIONS.campus.list);
  const showFaculties = can(PERMISSIONS.faculty.list);
  const showDepartments = can(PERMISSIONS.department.list);
  const showEvents = can(PERMISSIONS.event.list);
  const showClubs = can(PERMISSIONS.club.list);
  const showUsers = can(PERMISSIONS.user.list);
  const showAnalytics = can(PERMISSIONS.analytics.view);

  const showUniversityInfo = can(PERMISSIONS.university.get);
  const showFinance = can(PERMISSIONS.transaction.list);

  const showLabels = !isCollapsed || (isMobile && isMobileMenuOpen);

  const NavItem = ({ to, icon, label, badge }) => (
    <li className="nav-item">
      <Link
        to={to}
        className={`nav-link-modern d-flex align-items-center ${isActiveRoute(to) ? "active" : ""}`}
        title={!showLabels ? label : ""}
        onClick={() => isMobile && setIsMobileMenuOpen(false)}
      >
        <div className="nav-icon">
          <Icon name={icon.replace("fas fa-", "")} size="md" />
        </div>
        {showLabels && (
          <div className="nav-content">
            <span className="nav-title">{label}</span>
            {badge > 0 && <span className="um-pending-badge">{badge}</span>}
          </div>
        )}
      </Link>
    </li>
  );

  return (
    <>
      {/* Mobile Menu Toggle Button */}
      {isMobile && (
        <button
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <Icon name={isMobileMenuOpen ? "times" : "bars"} size="md" />
        </button>
      )}

      <div
        className={`sidebar-modern um-sidebar ${
          isCollapsed ? "sidebar-collapsed" : "sidebar-expanded"
        } ${
          isMobile ? (isMobileMenuOpen ? "mobile-open" : "mobile-closed") : ""
        }`}
      >
        {/* Sidebar Header */}
        <div className="sidebar-header-modern">
          {showLabels && (
            <div className="brand-section">
              <div className="brand-info">
                <h6 className="brand-title">{university?.name || t('branding.universityMgr')}</h6>
              </div>
            </div>
          )}

          <button
            className="sidebar-toggle"
            onClick={onToggle}
            title={t(isCollapsed ? 'tooltips.expandSidebar' : 'tooltips.collapseSidebar')}
          >
            <Icon
              name={isCollapsed ? "chevron-right" : "chevron-left"}
              size="md"
            />
          </button>
        </div>

        {/* Sidebar Body */}
        <div className="sidebar-body-modern">
          {/* University Setup */}
          <nav className="main-nav">
            {showLabels && <div className="nav-section-title">{t('sections.universitySetup')}</div>}
            <ul className="nav-list">
              <NavItem to={`${basePath}/dashboard`} icon="fas fa-tachometer-alt" label={t('universityManager.dashboard')} />
              {showUniversityInfo && (
                <NavItem to={`${basePath}/university-info`} icon="fas fa-info-circle" label={t('universityManager.universityInfo')} />
              )}
              {showCampuses && (
                <NavItem to={`${basePath}/campuses`} icon="fas fa-building" label={t('universityManager.campuses')} />
              )}
              {showFaculties && (
                <NavItem to={`${basePath}/faculties`} icon="fas fa-graduation-cap" label={t('universityManager.faculties')} />
              )}
              {showDepartments && (
                <NavItem to={`${basePath}/departments`} icon="fas fa-sitemap" label={t('universityManager.departments')} />
              )}
            </ul>
          </nav>

          {/* Events */}
          {showEvents && (
            <nav>
              {showLabels && <div className="nav-section-title">{t('menu.events')}</div>}
              <ul className="nav-list">
                <NavItem
                  to={`${basePath}/events/awaiting`}
                  icon="fas fa-clock"
                  label={t('universityManager.awaitingApproval')}
                  badge={pendingCount}
                />
                <NavItem to={`${basePath}/events/all`} icon="fas fa-calendar-alt" label={t('universityManager.allEvents')} />
              </ul>
            </nav>
          )}

          {/* Clubs */}
          {showClubs && (
            <nav>
              {showLabels && <div className="nav-section-title">{t('menu.clubs')}</div>}
              <ul className="nav-list">
                <NavItem to={`${basePath}/clubs`} icon="fas fa-users" label={t('universityManager.clubs')} />
              </ul>
            </nav>
          )}

          {/* Users */}
          {showUsers && (
            <nav>
              {showLabels && <div className="nav-section-title">{t('sections.userManagement')}</div>}
              <ul className="nav-list">
                <NavItem to={`${basePath}/users`} icon="fas fa-user-friends" label={t('universityManager.users')} />
              </ul>
            </nav>
          )}

          {/* Authority */}
          {showAuthorityNav && (
            <nav>
              {showLabels && <div className="nav-section-title">{t('sections.authority')}</div>}
              <ul className="nav-list">
                <NavItem to={`${basePath}/authority-management`} icon="fas fa-user-shield" label={t('universityManager.authorityManagement')} />
              </ul>
            </nav>
          )}

          {/* Finance */}
          {showFinance && (
            <nav>
              {showLabels && <div className="nav-section-title">{t('sections.finance')}</div>}
              <ul className="nav-list">
                <NavItem to={`${basePath}/finance`} icon="fas fa-chart-line" label={t('universityManager.transfers')} />
              </ul>
            </nav>
          )}

          {/* Analytics */}
          {showAnalytics && (
            <nav>
              {showLabels && <div className="nav-section-title">{t('sections.analytics')}</div>}
              <ul className="nav-list">
                <NavItem to={`${basePath}/analytics`} icon="fas fa-chart-pie" label={t('universityManager.analytics')} />
              </ul>
            </nav>
          )}



          {/* Bottom Nav - Logout */}
          <nav className="bottom-nav">
            <ul className="nav-list">
              <li className="nav-item">
                <button
                  type="button"
                  className="nav-link-modern d-flex align-items-center w-100 border-0 bg-transparent text-start"
                  title={!showLabels ? t('menu.logout', 'Logout') : ""}
                  onClick={() => {
                    if (isMobile) setIsMobileMenuOpen(false);
                    handleLogout();
                  }}
                >
                  <div className="nav-icon">
                    <Icon name="sign-out-alt" size="md" />
                  </div>
                  {showLabels && (
                    <div className="nav-content">
                      <span className="nav-title">{t('menu.logout', 'Logout')}</span>
                    </div>
                  )}
                </button>
              </li>
            </ul>
          </nav>

        </div>
      </div>

      {/* Mobile Overlay */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>
  );
};

export default UniversityManagerSidebar;
